import * as Sentry from "@sentry/react";

// App version for release tracking
const APP_VERSION = "1.0.0";

export const initSentry = () => {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  
  // Skip initialization if no DSN is configured
  if (!dsn) {
    console.log("[Sentry] No DSN configured, skipping initialization");
    return;
  }

  Sentry.init({
    dsn: dsn,
    
    // Environment and release tracking
    environment: process.env.NODE_ENV || "development",
    release: APP_VERSION,
    
    // Performance monitoring - capture 10% in production, 100% in dev
    tracesSampleRate: process.env.NODE_ENV === "production" ? 0.1 : 1.0,
    
    // Enable replay for session recording (optional, can be removed if not needed)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    
    // Don't send PII by default
    sendDefaultPii: false,
    
    // Filter out noisy errors
    beforeSend(event, hint) {
      // Filter out errors from browser extensions
      if (event.exception) {
        const error = hint.originalException;
        if (error?.message?.includes("chrome-extension") ||
            error?.message?.includes("Script error") ||
            error?.message?.includes("ResizeObserver")) {
          return null;
        }
      }
      
      // Remove sensitive query parameters from URLs
      if (event.request?.url) {
        try {
          const url = new URL(event.request.url);
          url.searchParams.delete("token");
          url.searchParams.delete("session_token");
          url.searchParams.delete("api_key");
          event.request.url = url.toString();
        } catch (e) {
          // URL parsing failed, continue with original
        }
      }
      
      return event;
    },
    
    // Configure which URLs should be traced for distributed tracing
    tracePropagationTargets: [
      "localhost",
      /^\//,
      /\.emergentagent\.com/,
      /\.fitxess\.com/,
    ],
  });
  
  // Set default tags
  Sentry.setTag("platform", "web");
  Sentry.setTag("app_version", APP_VERSION);
  
  console.log("[Sentry] Initialized successfully");
};

// Identify user for error tracking
export const identifyUser = (userId) => {
  if (!userId) {
    Sentry.setUser(null);
    return;
  }
  
  Sentry.setUser({
    id: userId,
  });
};

// Clear user context on logout
export const clearUserContext = () => {
  Sentry.setUser(null);
};

// Capture exceptions with custom context
export const captureException = (error, context = {}) => {
  Sentry.captureException(error, {
    tags: context.tags || {},
    extra: context.extra || {},
    level: context.level || "error",
  });
};

// Capture messages
export const captureMessage = (message, level = "info") => {
  Sentry.captureMessage(message, level);
};

// Add breadcrumb for user actions
export const addBreadcrumb = (category, message, data = {}) => {
  Sentry.addBreadcrumb({
    category,
    message,
    level: "info",
    data,
  });
};

// Start a performance span for tracking specific operations
export const startSpan = (name, op, callback) => {
  return Sentry.startSpan({ name, op }, callback);
};

// Track plan generation performance
export const trackPlanGeneration = async (operation, callback) => {
  return Sentry.startSpan(
    { name: `Plan Generation: ${operation}`, op: "plan.generate" },
    callback
  );
};

// Track grocery list generation performance
export const trackGroceryGeneration = async (callback) => {
  return Sentry.startSpan(
    { name: "Grocery List Generation", op: "grocery.generate" },
    callback
  );
};

export default Sentry;
